import React from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MenuRounded, CheckCircleOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SIDEBAR_WIDTH = 320;

const SettingsSidebar = ({
  user,
  isAdmin,
  tabs,
  activeTab,
  onTabChange,
  mobileOpen,
  onDrawerToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const sidebarContent = (
    <Box data-tour="settings-sidebar" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* User profile section */}
      <Box
        sx={{
          p: 3,
          pb: 4,
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.1)}, ${theme.palette.background.paper})`,
          borderRadius: isSmall ? 0 : "0 0 24px 24px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Avatar
          sx={{
            width: 100,
            height: 100,
            bgcolor: theme.palette.primary.main,
            fontSize: 36,
            fontWeight: "bold",
            border: `4px solid ${theme.palette.background.paper}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 12px rgba(0,0,0,0.3)"
                : "0 4px 12px rgba(0,0,0,0.08)",
            mb: 2,
          }}
        >
          {user?.first_name?.[0]}
          {user?.last_name?.[0]}
        </Avatar>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {user?.first_name} {user?.last_name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          {user?.email}
        </Typography>

        <Chip
          icon={isAdmin ? <CheckCircleOutline fontSize="small" /> : null}
          label={isAdmin ? t("admin_account") : t("user_account")}
          size="small"
          sx={{
            bgcolor: isAdmin
              ? alpha(theme.palette.success.main, 0.1)
              : alpha(theme.palette.primary.main, 0.1),
            color: isAdmin
              ? theme.palette.success.main
              : theme.palette.primary.main,
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: isAdmin
                ? theme.palette.success.main
                : theme.palette.primary.main,
            },
          }}
        />
      </Box>

      {/* Navigation menu */}
      <List sx={{ px: 2, py: 1, flexGrow: 1 }}>
        {tabs.map((tab) => (
          <ListItem
            key={tab.id}
            data-tour={tab.id === 1 ? "settings-prompts-tab" : undefined}
            button
            disableRipple
            onClick={() => onTabChange(tab.id)}
            sx={{
              borderRadius: "12px",
              mb: 1,
              py: 1.5,
              transition: "all 0.2s",
              bgcolor:
                activeTab === tab.id
                  ? alpha(theme.palette.primary.main, 0.1)
                  : "transparent",
              color:
                activeTab === tab.id
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              "&:hover": {
                bgcolor:
                  activeTab === tab.id
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.divider, 0.5),
                color:
                  activeTab === tab.id
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: 40,
                "& svg": { fontSize: "1.35rem" },
              }}
            >
              {tab.icon}
            </ListItemIcon>
            <ListItemText
              primary={tab.label}
              primaryTypographyProps={{
                fontSize: "1rem",
                fontWeight: activeTab === tab.id ? 600 : 500,
              }}
            />
            {activeTab === tab.id && (
              <Box
                sx={{
                  width: 4,
                  height: 24,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "4px",
                }}
              />
            )}
          </ListItem>
        ))}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: "auto",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: theme.palette.text.secondary,
          }}
        >
          © {new Date().getFullYear()} {t("company_name")}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuRounded />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {t("settings")}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: SIDEBAR_WIDTH,
              minWidth: SIDEBAR_WIDTH,
              maxWidth: SIDEBAR_WIDTH,
              bgcolor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              boxSizing: "border-box",
              flexShrink: 0,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Paper
          elevation={0}
          sx={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            maxWidth: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "auto",
          }}
        >
          {sidebarContent}
        </Paper>
      )}
    </>
  );
};

export default SettingsSidebar;
